import { useState, type MouseEvent, type ReactNode } from 'react'
import {
  CameraOutlined,
  CloudServerOutlined,
  CloudUploadOutlined,
  DesktopOutlined,
  ExportOutlined,
  FileTextOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  MoreOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  VideoCameraOutlined,
  WifiOutlined,
} from '@ant-design/icons'
import { Dropdown, Popconfirm, Space } from 'antd'
import type { MenuProps } from 'antd'
import { Button, toast } from '@crosscode/gravity-components'
import { api } from '@/api/client'
import { useRemoteDesktop } from '@/features/remote/RemoteDesktopHost'
import { useFtpClient } from '@/features/ftp/FtpClientHost'
import { IntegrationLogsModal } from './IntegrationLogsModal'
import { fetchNodeRdp } from './nodeRemote'
import { NODE_META } from './constants'
import type { InfraNode, NodeType } from './utils'
import { labelToType } from './utils'

export const NODE_ICONS: Record<NodeType, ReactNode> = {
  cash_server: <CloudServerOutlined />,
  cm_server: <CloudServerOutlined />,
  mikrotik: <WifiOutlined />,
  integration: <LinkOutlined />,
  egais_utm: <SafetyCertificateOutlined />,
  chz: <SafetyCertificateOutlined />,
  unknown: <GlobalOutlined />,
}

function ActionMenu({ items }: { items: MenuProps['items'] }) {
  if (!items?.length) return null
  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
    </Dropdown>
  )
}

export function DeviceActions({
  node,
  id,
  storeId,
  chz,
  onReload,
  onShowDetails,
}: {
  node: InfraNode
  id: string
  storeId: string
  chz?: Record<string, unknown>
  onReload: () => void
  onShowDetails?: () => void
}) {
  const { openRemote } = useRemoteDesktop()
  const { openFtp } = useFtpClient()
  const [integrationLogsOpen, setIntegrationLogsOpen] = useState(false)
  const nodeType = labelToType(node.label || node.name, node.type)
  const title = node.name || node.label || NODE_META[nodeType].titleHint
  const integrationLogsUrl =
    id !== 'legacy' ? `/api/nodes/${id}/logs` : `/api/stores/${storeId}/logs`

  const stop = (e: MouseEvent) => e.stopPropagation()

  const openNodeRdp = async () => {
    if (id === 'legacy') {
      toast.error('RDP недоступен для legacy-узла — задайте узел в настройках')
      return
    }
    const conn = await fetchNodeRdp(id)
    if (!conn?.connectionToken && !conn?.url) return
    openRemote({
      title,
      type: 'RDP',
      tunnelUrl: conn.tunnelUrl,
      connectionToken: conn.connectionToken,
      url: conn.url,
    })
  }

  if (nodeType === 'cash_server') {
    const menuItems: MenuProps['items'] = [
      {
        key: 'supermag',
        icon: <ExportOutlined />,
        label: 'СуперМаг',
        onClick: async () => {
          try {
            const data = await api<{ url?: string }>(
              id !== 'legacy'
                ? `/api/nodes/${id}/supermag-ukm4-login`
                : `/api/stores/${storeId}/supermag-ukm4-login`,
            )
            if (data.url) window.open(data.url, '_blank')
            else toast.error('URL не получен')
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Ошибка')
          }
        },
      },
    ]
    return (
      <Space size={4} onClick={stop}>
        <Button size="small" icon={<DesktopOutlined />} onClick={() => void openNodeRdp()}>
          RDP
        </Button>
        <ActionMenu items={menuItems} />
      </Space>
    )
  }

  if (nodeType === 'cm_server') {
    return (
      <Space size={4} onClick={stop}>
        <Button size="small" icon={<DesktopOutlined />} onClick={() => void openNodeRdp()}>
          RDP
        </Button>
      </Space>
    )
  }

  if (nodeType === 'integration') {
    const menuItems: MenuProps['items'] = [
      {
        key: 'logs',
        icon: <FileTextOutlined />,
        label: 'Логи',
        onClick: () => setIntegrationLogsOpen(true),
      },
      {
        key: 'ftp',
        icon: <CloudUploadOutlined />,
        label: 'FTP',
        onClick: () =>
          openFtp({
            connectionType: 'mis',
            host: node.ip!,
            title: `FTP · ${title}`,
          }),
      },
    ]
    return (
      <>
        <Space size={4} onClick={stop}>
          <Popconfirm
            title="Действительно хотите перезагрузить модуль интеграции?"
            okText="Да"
            cancelText="Отмена"
            onConfirm={async () => {
              try {
                await api(
                  id !== 'legacy' ? `/api/nodes/${id}/restart` : `/api/stores/${storeId}/restart`,
                  { method: 'POST' },
                )
                toast.success('Перезагрузка отправлена')
                onReload()
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Ошибка')
              }
            }}
          >
            <Button size="small" danger icon={<ReloadOutlined />}>
              Рестарт
            </Button>
          </Popconfirm>
          <ActionMenu items={menuItems} />
        </Space>
        <IntegrationLogsModal
          open={integrationLogsOpen}
          title={title}
          logsUrl={integrationLogsUrl}
          onClose={() => setIntegrationLogsOpen(false)}
        />
      </>
    )
  }

  if (nodeType === 'mikrotik' && node.ip) {
    return (
      <Space size={4} onClick={stop}>
        <Popconfirm
          title="Действительно хотите перезагрузить MikroTik?"
          okText="Да"
          cancelText="Отмена"
          onConfirm={async () => {
            try {
              await api(
                id !== 'legacy' ? `/api/nodes/${id}/reboot` : `/api/stores/${storeId}/reboot-mikrotik`,
                { method: 'POST' },
              )
              toast.success('Команда reboot отправлена')
            } catch (e) {
              toast.error(e instanceof Error ? e.message : 'Ошибка')
            }
          }}
        >
          <Button size="small" danger icon={<PoweroffOutlined />}>
            Reboot
          </Button>
        </Popconfirm>
      </Space>
    )
  }

  if (nodeType === 'chz' || nodeType === 'egais_utm') {
    const needsInit = nodeType === 'chz' && chz?.status === 'not_configured'
    return (
      <Space size={4} onClick={stop}>
        {needsInit ? (
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={async () => {
              try {
                await api(
                  id !== 'legacy' ? `/api/nodes/${id}/chz/init` : `/api/stores/${storeId}/chz/init`,
                  { method: 'POST' },
                )
                toast.success('Инициализация запущена')
                onReload()
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Ошибка')
              }
            }}
          >
            Инициализация
          </Button>
        ) : null}
        {onShowDetails ? (
          <Button
            size="small"
            icon={<InfoCircleOutlined />}
            onClick={() => onShowDetails()}
          >
            Подробнее
          </Button>
        ) : null}
      </Space>
    )
  }

  return null
}

export function PriceCheckerActions({ checkerId }: { checkerId: string | number }) {
  const stop = (e: MouseEvent) => e.stopPropagation()

  const menuItems: MenuProps['items'] = [
    {
      key: 'cam',
      icon: <VideoCameraOutlined />,
      label: 'Камера',
      onClick: () =>
        window.open(`/api/price-checkers/${checkerId}/camshot?t=${Date.now()}`, '_blank'),
    },
    {
      key: 'restart',
      icon: <ReloadOutlined />,
      label: 'Рестарт',
      danger: true,
      onClick: async () => {
        try {
          await api(`/api/price-checkers/${checkerId}/restart`, { method: 'POST' })
          toast.success('Рестарт отправлен')
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Ошибка')
        }
      },
    },
    {
      key: 'load-url',
      icon: <LinkOutlined />,
      label: 'Загрузить URL',
      onClick: async () => {
        try {
          await api(`/api/price-checkers/${checkerId}/load-url`, { method: 'POST' })
          toast.success('Начальная страница загружена')
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Ошибка загрузки URL')
        }
      },
    },
  ]

  return (
    <Space size={4} onClick={stop}>
      <Button
        size="small"
        icon={<CameraOutlined />}
        onClick={() =>
          window.open(`/api/price-checkers/${checkerId}/screenshot?t=${Date.now()}`, '_blank')
        }
      >
        Скриншот
      </Button>
      <ActionMenu items={menuItems} />
    </Space>
  )
}
